import { Fragment, type ReactNode } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
}

const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
}: ModalProps) => {
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-4xl',
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={closeOnOverlayClick ? onClose : () => {}}
      >
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-4"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <DialogPanel
                className={cn(
                  'w-full transform overflow-hidden rounded-xl text-left align-middle transition-all',
                  // Light mode
                  'bg-white shadow-xl',
                  // Dark mode
                  'dark:bg-secondary-900 dark:border dark:border-white/[0.08]',
                  'dark:shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_20px_50px_-10px_rgba(0,0,0,0.5)]',
                  sizes[size]
                )}
              >
                {/* Header */}
                {(title || showCloseButton) && (
                  <div className={cn(
                    'flex items-start justify-between px-5 pt-5',
                    description ? 'pb-2' : 'pb-4'
                  )}>
                    <div className="flex-1">
                      {title && (
                        <DialogTitle
                          as="h3"
                          className="text-base font-semibold text-gray-900 dark:text-secondary-100"
                        >
                          {title}
                        </DialogTitle>
                      )}
                      {description && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-secondary-400">
                          {description}
                        </p>
                      )}
                    </div>
                    {showCloseButton && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={onClose}
                        className="-mr-1 -mt-1 text-gray-400 hover:text-gray-500 dark:text-secondary-500 dark:hover:text-secondary-300"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}

                {/* Content */}
                <div className={cn(
                  'px-5 pb-5',
                  !(title || showCloseButton) && 'pt-5'
                )}>
                  {children}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Modal Footer Component
interface ModalFooterProps {
  children: ReactNode;
  className?: string;
  divider?: boolean;
}

const ModalFooter = ({ children, className, divider = false }: ModalFooterProps) => (
  <div
    className={cn(
      'flex justify-end gap-2 pt-4',
      divider && 'border-t border-gray-100 dark:border-white/[0.06] mt-4',
      className
    )}
  >
    {children}
  </div>
);

export { Modal, ModalFooter };
